
  import {
    defineComponent,
    ref,
    onMounted,
    onBeforeUnmount,
    computed,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useAbility } from '@casl/vue';
  import { useI18n } from 'vue-i18n/index';
  import { Actions } from '@/store/enums/StoreEnums';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';

  export default defineComponent({
    name: 'packages-listing',
    components: {
      Datatable,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (key) => (te(key) ? t(key) : key);

      const loading = ref(false);

      const tableHeader = ref([
        { name: translate('actions'), key: 'actions' },
        { name: translate('ID'), key: 'id', sortable: true },
        { name: translate('titleEn'), key: 'titleEn', sortable: true },
        { name: translate('titleAr'), key: 'titleAr', sortable: true },
        { name: translate('Type'), key: 'slug', sortable: true },
        { name: translate('originalPrice'), key: 'price', sortable: true },
        {
          name: translate('offeredPrice'),
          key: 'offeredPrice',
          sortable: true,
        },
        { name: translate('validity'), key: 'daysCovered', sortable: true },
        { name: translate('adsCredits'), key: 'adsCredits', sortable: true },
        { name: translate('iveCredits'), key: 'iveCredits', sortable: true },
        { name: translate('sort'), key: 'sort', sortable: true },
        { name: translate('status'), key: 'isActive', sortable: true },
      ]);

      const fetchPackages = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_PACKAGES);
        loading.value = false;
      };

      const editPackage = (id: number) => {
        router.push({ name: 'package-editing', params: { id } });
      };

      const goTo = (pageName: string) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val: number) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PACKAGES_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_PACKAGES);
        loading.value = false;
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('packages'), [
          translate('subscriptions'),
        ]);
        await fetchPackages();
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_PACKAGE_STORE);
      });

      const toggleStatusPackage = async (ads) => {
        const deletedMessage = ads.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });
        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_PACKAGE, ads.id);
          await store.dispatch(Actions.GET_PACKAGES);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = ads.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      return {
        tableData: computed(() => store.getters.packagesList),
        totalItems: computed(() => store.getters.packagesListCount),
        tableHeader,
        loading,
        toggleStatusPackage,
        translate,
        editPackage,
        goTo,
        pageChanged,
        can,
      };
    },
  });
